import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { AccessibleIcon } from "@offstreetparking/design-system/accessibility";
import { LinkButton } from "@offstreetparking/design-system/forms";
import { ChevronRightIcon } from "@offstreetparking/design-system/icons/ChevronRightIcon";
import { EditIcon } from "@offstreetparking/design-system/icons/EditIcon";
import { ParkingIcon } from "@offstreetparking/design-system/icons/ParkingIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { classNames } from "@offstreetparking/design-system/utils";
import { type VariantProps, cva } from "class-variance-authority";
import type { KeyboardEvent, SyntheticEvent } from "react";
import { useDisclosure } from "../../../../hooks/useDisclosure";
import { CapacityFullBadge } from "../../../dataDisplay/CapacityFullBadge";
import { getLocalizedLargestDurationString } from "../../../dataDisplay/parkingDetails/getLocalizedLargestDurationString";
import { ResponsiveOverlay } from "../../../overlays/ResponsiveOverlay";
import type { WizardLocation } from "../../PermitContextProvider/types";
import { LocationWithDirections } from "./LocationWithDirections";
import { splitLocationsForDisplay } from "./splitLocationsForDisplay";

const preventParentCardSelection = (event: SyntheticEvent) => {
	event.stopPropagation();
};

const preventParentCardKeyboardSelection = (event: KeyboardEvent) => {
	if (event.key === "Enter" || event.key === " ") {
		event.stopPropagation();
	}
};
const locationButtonVariants = cva("flex w-full items-center justify-between rounded-md p-4", {
	variants: {
		variant: {
			default: "border border-neutral",
			secondary: "gap-2 bg-neutral-faded",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});
type LocationButtonListProps = {
	locations: WizardLocation[];
	variant?: VariantProps<typeof locationButtonVariants>["variant"];
	onLocationClick?: (locationId: number) => void;
	collapsible?: boolean;
};
export const LocationButtonList = ({ locations, variant, onLocationClick, collapsible }: LocationButtonListProps) => {
	const { i18n, t } = useLingui();
	const [isOverlayOpen, openOverlay, closeOverlay] = useDisclosure();
	const { ordered, visible, hiddenCount: hiddenLocationCount } = splitLocationsForDisplay({ locations });
	const displayedLocations = collapsible ? visible : ordered;
	return (
		<div>
			{locations.length > 1 && variant === "secondary" && (
				<div className="mb-2 flex items-center gap-2">
					<AccessibleIcon label={t`Park at any of the following locations`}>
						<ParkingIcon className="mt-0.5 h-4 min-h-4 w-4 min-w-4" />
					</AccessibleIcon>
					<Text weight="bold" size="sm">
						{t`Park at any of the following locations`}
					</Text>
				</div>
			)}

			<div className="space-y-2">
				{displayedLocations.map((location) => {
					const seconds = location.lot?.distancesFromPlaces?.timeSeconds ?? 0;
					const localizedDuration = getLocalizedLargestDurationString(seconds, i18n);
					const disabled = location.capacity === "full" && variant === "default";
					if (!onLocationClick) {
						return (
							<div
								key={location.id}
								className={locationButtonVariants({
									variant,
								})}
							>
								<LocationWithDirections location={location} localizedDuration={localizedDuration} />
							</div>
						);
					}
					return (
						<button
							type="button"
							key={location.id}
							className={locationButtonVariants({
								variant,
							})}
							onClick={(e) => {
								e.stopPropagation();
								onLocationClick(location.id);
							}}
							disabled={disabled}
						>
							<div className="flex flex-col items-start gap-2 overflow-hidden">
								{location.capacity === "full" && <CapacityFullBadge />}
								<LocationWithDirections
									location={location}
									localizedDuration={localizedDuration}
									isDisabled={disabled}
									isInsideInteractiveElement
								/>
							</div>

							{variant === "default" ? (
								<ChevronRightIcon
									className={classNames("size-7 text-primary", disabled ? "opacity-50" : "")}
								/>
							) : (
								<EditIcon className="size-4 shrink-0 text-accent" />
							)}
						</button>
					);
				})}
				{collapsible && hiddenLocationCount > 0 && (
					<div onClick={preventParentCardSelection} onKeyDown={preventParentCardKeyboardSelection}>
						<LinkButton size="sm" type="button" onClick={openOverlay}>
							{t({
								comment:
									"Link under a shortened list of parking locations; opens an overlay with the full list",
								message: plural(hiddenLocationCount, {
									one: "View # more location",
									other: "View # more locations",
								}),
							})}
						</LinkButton>
						<ResponsiveOverlay
							isOpen={isOverlayOpen}
							onOpenChange={(isOpen) => {
								if (!isOpen) {
									closeOverlay();
								}
							}}
							title={t`Park at any of the following locations`}
						>
							<LocationButtonList locations={locations} variant="default" />
						</ResponsiveOverlay>
					</div>
				)}
			</div>
		</div>
	);
};
